import React, { useState, useEffect } from 'react';
import { Spin, Alert } from 'antd';
import { useParams } from 'react-router-dom';
import { menuAPI } from '../services/api';

const UNSAFE_SCRIPT_PATTERNS = [
  { pattern: /<script[^>]*\bsrc\s*=\s*["']https?:\/\//i, reason: '不允许加载远程脚本' },
  { pattern: /\beval\s*\(/i, reason: '不允许使用 eval' },
  { pattern: /\bnew\s+Function\s*\(/i, reason: '不允许动态构造函数执行' },
  { pattern: /document\.cookie/i, reason: '不允许访问 cookie' },
  { pattern: /\blocalStorage\b/i, reason: '不允许访问 localStorage' },
  { pattern: /\bsessionStorage\b/i, reason: '不允许访问 sessionStorage' },
  { pattern: /\bindexedDB\b/i, reason: '不允许访问 indexedDB' },
  { pattern: /\bXMLHttpRequest\b/i, reason: '不允许发起 XHR 请求' },
  { pattern: /\bfetch\s*\(/i, reason: '不允许发起网络请求' },
  { pattern: /\bWebSocket\b/i, reason: '不允许建立 WebSocket 连接' },
  { pattern: /window\.(top|parent)/i, reason: '不允许访问上层窗口' },
  { pattern: /\bpostMessage\s*\(/i, reason: '不允许跨窗口消息通信' },
];

const detectUnsafeScript = (content) => {
  for (const rule of UNSAFE_SCRIPT_PATTERNS) {
    if (rule.pattern.test(content)) {
      return rule.reason;
    }
  }
  return null;
};

const injectCSP = (content) => {
  const cspMeta = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; script-src-attr 'unsafe-inline'; script-src-elem 'unsafe-inline'; style-src 'unsafe-inline'; img-src data: blob: https: http:; font-src data: https: http:; media-src data: blob: https: http:; connect-src 'none'; object-src 'none'; frame-src 'none'; form-action 'none'; base-uri 'none';">`;

  if (/<head[^>]*>/i.test(content)) {
    return content.replace(/<head[^>]*>/i, (match) => `${match}${cspMeta}`);
  }

  return `<head>${cspMeta}</head>${content}`;
};

const FileViewer = () => {
  const { menuId } = useParams();
  const [fileContent, setFileContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchFileContent = async () => {
      try {
        setLoading(true);
        const response = await menuAPI.readMenuFile(menuId);
        const data = response.data || response;
        let content = data.content || '';

        // 仅处理真正的 body 选择器，避免误伤 .history-body 等类名
        content = content.replace(/(^|[\s}])body\s*\{([^}]*)\}/gi, (match, prefix, bodyRules) => {
          return `${prefix}body { ${bodyRules} margin: 0 !important; padding: 0 !important; }`;
        });

        const unsafeReason = detectUnsafeScript(content);
        if (unsafeReason) {
          throw new Error(`检测到不安全脚本：${unsafeReason}`);
        }

        const securedContent = injectCSP(content);
        setFileContent(securedContent);
      } catch (err) {
        console.error('读取文件失败:', err);
        setError(err.message || '无法读取文件内容');
      } finally {
        setLoading(false);
      }
    };

    if (menuId) {
      fetchFileContent();
    }
  }, [menuId]);

  if (loading) {
    return (
      <div style={{ padding: isMobile ? '12px' : '24px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: isMobile ? '12px' : '24px' }}>
        <Alert message="错误" description={error} type="error" showIcon />
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '12px' : '24px',
      background: '#0d1117',
      color: '#e6edf3',
      minHeight: 'calc(100vh - 64px)'
    }}>
      <iframe
        title={`menu-file-${menuId}`}
        srcDoc={fileContent}
        sandbox="allow-scripts"
        referrerPolicy="no-referrer"
        style={{
          width: '100%',
          border: 'none',
          minHeight: 'calc(100vh - 120px)',
          background: '#0d1117',
          padding: isMobile ? '8px' : '16px',
          overflow: 'auto',
          borderRadius: '8px'
        }}
      />
    </div>
  );
};

export default FileViewer;
